import { useRef, useState } from 'react';
import { usePrintEditorStore } from '../state/usePrintEditorStore';
import { importArtworkFile } from '../utils/importArtwork';

/**
 * Bringing a design file (AI / PSD / PDF / SVG / TIFF) into the document.
 *
 * Lives in a hook because two places offer it — the Import tile in the Elements
 * grid and the Import button on the icon rail — and an import is a long, alerting,
 * page-resizing operation that must behave identically wherever it was started
 * from. Each caller renders its own hidden <input>, since a shared one would have
 * to be lifted above both.
 */

export const ARTWORK_ACCEPT = [
    '.ai', '.pdf', '.svg', '.psd', '.psb', '.tif', '.tiff',
    'application/pdf', 'image/svg+xml', 'image/vnd.adobe.photoshop', 'image/tiff',
].join(',');

export const ARTWORK_TITLE = 'Import artwork — Illustrator (.ai), Photoshop (.psd/.psb), PDF, SVG or TIFF.\n'
    + 'AI / PSD / SVG keep their layer structure; TIFF imports as one flat layer.';

export function useArtworkImport() {
    const inputRef = useRef(null);
    const [importing, setImporting] = useState(false);

    /**
     * `resize` is what separates the Import button from the image picker: the
     * Import button exists to bring a whole artwork in, so the page takes the
     * artwork's size. A design file that merely arrived through the image picker
     * (an .svg logo, say) must not silently resize a page already being worked
     * on — it lands centred on the current page instead.
     */
    const runArtworkImport = async (file, { resize = true } = {}) => {
        if (!file) return;
        setImporting(true);
        try {
            const result = await importArtworkFile(file);
            if (!result.elements.length) throw new Error('Nothing importable found in this file.');
            const store = usePrintEditorStore.getState();
            const fitPage = resize || store.document.elements.length === 0;
            if (fitPage) {
                store.importElements(result.elements, result.canvas, result.groups);
                // Re-fit the artboard to the viewport after resizing to the artwork.
                requestAnimationFrame(() => {
                    const vp = document.querySelector('[data-workspace="1"]');
                    if (vp) {
                        const r = vp.getBoundingClientRect();
                        usePrintEditorStore.getState().fitToView(r.width, r.height, { padding: 72 });
                    }
                });
            } else {
                const frame = store.activeFrame();
                const dx = Math.round(((frame?.width || result.canvas.width) - result.canvas.width) / 2);
                const dy = Math.round(((frame?.height || result.canvas.height) - result.canvas.height) / 2);
                store.importElements(
                    result.elements.map((el) => ({ ...el, x: (el.x || 0) + dx, y: (el.y || 0) + dy })),
                    null,
                    result.groups,
                    { frameId: frame?.id || null },
                );
            }
            const pageNote = result.pageCount > 1 ? ` (page 1 of ${result.pageCount})` : '';
            if (result.vector) {
                const source = /\.(psd|psb)$/i.test(file.name) ? 'Photoshop'
                    : (/\.svg$/i.test(file.name) ? 'SVG' : 'Illustrator');
                const textNote = result.extractedText > 0
                    ? ` ${result.extractedText} of them ${result.extractedText > 1 ? 'are' : 'is'} editable text.`
                    : '';
                // Deeply nested / very dense artwork comes in whole, but as shared
                // layers rather than one per object. Say so — a silent cap reads as
                // "some of my objects went missing".
                const mergedNote = result.mergedNodes > 0
                    ? `\n\n${result.mergedNodes} very deep or very dense objects were kept, `
                        + 'but grouped into shared layers instead of one layer each.'
                    : '';
                // eslint-disable-next-line no-alert
                window.alert(
                    `Imported ${result.layerCount} layer${result.layerCount > 1 ? 's' : ''}${pageNote}, `
                    + `keeping your ${source} group structure.${textNote}\n\n`
                    + 'Expand the layers in the Layers panel to reach nested sub-layers. '
                    + `Double-click on canvas drills in one level at a time.${mergedNote}`,
                );
            } else if (result.raster) {
                // eslint-disable-next-line no-alert
                window.alert(
                    `Imported as a flat, pixel-perfect artwork layer${pageNote}. No editable text `
                    + 'was found in this file.\n\nFor fully editable text and shapes, export your '
                    + 'Illustrator design as SVG (File → Export → Export As → SVG) and import that.',
                );
            } else if (result.extractedText > 0) {
                // eslint-disable-next-line no-alert
                window.alert(
                    `Imported ${result.extractedText} editable text object${result.extractedText > 1 ? 's' : ''}`
                    + ` + the artwork${pageNote}. Text is grouped under a "Text" layer.\n\n`
                    + 'Vector shapes/paths are kept in one "Artwork" layer — for editable shapes too, '
                    + 'export from Illustrator as SVG and import that.',
                );
            }
        } catch (err) {
            console.error('[print-editor] Artwork import failed', err);
            // eslint-disable-next-line no-alert
            window.alert(`Import failed: ${err?.message || err}`);
        } finally {
            setImporting(false);
        }
    };

    /** For the hidden <input>: import the picked file and free the input for a retry. */
    const onArtworkSelected = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        runArtworkImport(file);
    };

    const openPicker = () => inputRef.current?.click();

    return { importing, inputRef, openPicker, onArtworkSelected, runArtworkImport };
}
